/**
 * UserMenu — the avatar + its dropdown, shared by every shell and by the
 * first-run cloud pages (login's org-creation step, "Connect your first server").
 *
 * Owns everything that hangs off the avatar: the identity label, the
 * organization switcher (including pending invites and "create new org"),
 * the theme choice, and sign out. Hosts differ only in where the menu is
 * anchored and whether they offer the theme control, so those are
 * parameters rather than forks: a user stranded in an empty org reaches their
 * invitations from the setup pages through the same menu as from the shell.
 */

import { Check, LogOut, MonitorOff, Package, Plus, SunMoon } from 'lucide-react';
import type { ComponentProps, ReactElement } from 'react';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { api } from '@/api';
import { postRevokeAllSessions, postSession } from '@/api/signIn';
import { authSession } from '@/auth/authSession';
import { CreateOrgDialog } from '@/auth/CreateOrgDialog';
import { signOutGoogle } from '@/auth/google';
import { buildOrgMenu } from '@/auth/orgMenu';
import { reloadIntoOrg } from '@/auth/orgScope';
import { useAuth } from '@/auth/useAuth';
import { Hint } from '@/components/occurra/Hint';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/shadcn/avatar';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
    DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu';
import { useMe } from '@/hooks/useMe';
import { getFailureMessage } from '@/lib/failureMessage';
import { cn } from '@/lib/utils';
import type { AuthUser } from '@/types/auth';

import { ThemeRadioGroup } from './ThemeRadioGroup';

type MenuAlign = ComponentProps<typeof DropdownMenuContent>['align'];
type MenuSide = ComponentProps<typeof DropdownMenuContent>['side'];

function getInitials(user: AuthUser | null): string {
    const source = user?.name || user?.email || '';
    if (!source) {
        return 'CN';
    }
    const [firstText, second] = source.trim().split(/[\s@]+/).filter(Boolean);
    if (firstText === undefined) {
        return 'CN';
    }
    if (second === undefined) {
        return firstText.slice(0, 2).toUpperCase();
    }
    return (firstText.slice(0, 1) + second.slice(0, 1)).toUpperCase();
}

interface Props {
    shouldShowTheme?: boolean;
    shouldShowName?: boolean;
    align?: MenuAlign;
    side?: MenuSide;
}

export const UserMenu = ({
    // The setup pages offer no theme choice.
    shouldShowTheme = false,
    // Render the display name beside the avatar — the nav rail does this once
    // it's wide enough for labels.
    shouldShowName = false,
    align = 'end',
    side = 'bottom',
}: Props): ReactElement => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const me = useMe();
    const [isCreateOrgOpen, setCreateOrgOpen] = useState(false);
    const orgMenu = buildOrgMenu(me, me.activeOrgId);

    // Which menu sections render, in order. A section draws its separator only
    // when one of these precedes it, so hiding a section (the theme choice)
    // can't leave a doubled rule behind.
    const shouldShowIdentity = Boolean(user);
    const shouldShowOrgs = Boolean(
        !me.error && (orgMenu.active.length > 0 || orgMenu.invites.length > 0),
    );
    const shouldShowAdmin = Boolean(me.platformAdmin);

    // The session belongs to one organization: switching asks the cloud for the
    // other org's session (the cookie is the credential) before landing there.
    async function enterOrg(orgId: string): Promise<void> {
        if (orgId === authSession.getActiveOrg()) {
            return;
        }
        try {
            const session = await postSession(null, orgId);
            authSession.setToken(session.token);
        } catch (error) {
            toast.error(getFailureMessage(error, 'Could not open organization'));
            return;
        }
        reloadIntoOrg(orgId);
    }

    async function acceptInvite(orgId: string): Promise<void> {
        try {
            const me = await api.acceptInvite(orgId);
            if (me.token) {
                authSession.setToken(me.token);
            }
            reloadIntoOrg(orgId);
        } catch (error) {
            toast.error(getFailureMessage(error, 'Could not join organization'));
        }
    }

    const signOutEverywhere = async (): Promise<void> => {
        try {
            await postRevokeAllSessions();
        } catch (error) {
            toast.error(getFailureMessage(error, 'Could not sign out on all devices'));
            return;
        }
        signOutGoogle(user?.email);
        authSession.clearSession();
        navigate('/login', { replace: true });
    };

    const displayName = user?.name || user?.email || '';
    return (
        <>
            <DropdownMenu>
                {/* The name stands in for the tooltip once it shows. */}
                <Hint label={shouldShowName ? undefined : 'Open user menu'}>
                    <DropdownMenuTrigger asChild>
                        <button
                            type="button"
                            aria-label="Open user menu"
                            className={cn(
                                `group/avatar-button flex min-w-0 items-center rounded-full
                                focus:outline-none focus-visible:ring-2
                                focus-visible:ring-occ-brand`,
                                shouldShowName && 'gap-2',
                            )}
                        >
                            {/* The avatar would hide a hover fill behind it, so the hover
                                layer is drawn over the avatar. */}
                            <Avatar className={`size-9 shrink-0 after:absolute after:inset-0
                                group-hover/avatar-button:after:bg-occ-foreground-25`}
                            >
                                {user?.picture && (
                                    <AvatarImage
                                        src={user.picture}
                                        alt={user.name || user.email}
                                    />
                                )}
                                <AvatarFallback className={`bg-occ-foreground-25 text-sm
                                    font-semibold text-occ-foreground-main`}
                                >
                                    {getInitials(user)}
                                </AvatarFallback>
                            </Avatar>
                            {shouldShowName && (
                                <span className={`min-w-0 truncate text-sm
                                    text-occ-foreground-main`}
                                >
                                    {displayName}
                                </span>
                            )}
                        </button>
                    </DropdownMenuTrigger>
                </Hint>
                <DropdownMenuContent
                    align={align}
                    side={side}
                    className="min-w-[200px]"
                >
                    {shouldShowIdentity && user && (
                        <DropdownMenuLabel className="flex flex-col gap-0.5">
                            <span className="text-sm font-medium">{user.name || user.email}</span>
                            {user.name && (
                                <span className="text-xs font-normal text-occ-foreground-tertiary">
                                    {user.email}
                                </span>
                            )}
                        </DropdownMenuLabel>
                    )}
                    {shouldShowOrgs && (
                        <>
                            {shouldShowIdentity && <DropdownMenuSeparator />}
                            <DropdownMenuLabel className={`text-xs font-normal uppercase
                                    tracking-wide text-occ-foreground-tertiary`}
                            >
                                Organizations
                            </DropdownMenuLabel>
                            <div className="max-h-56 overflow-y-auto">
                                {orgMenu.active.map((org) => (
                                    <DropdownMenuItem
                                        key={org.orgId}
                                        className={org.isCurrent ? 'cursor-default' : undefined}
                                        onClick={(): void => { enterOrg(org.orgId); }}
                                    >
                                        {org.isCurrent
                                            ? <Check className="size-4 text-occ-brand" />
                                            : <span className="size-4" />}
                                        <span className="flex-1 truncate">{org.orgName}</span>
                                        {org.role && (
                                            <span className="text-xs text-occ-foreground-tertiary">
                                                {org.role}
                                            </span>
                                        )}
                                    </DropdownMenuItem>
                                ))}
                                {orgMenu.invites.map((invite) => (
                                    <DropdownMenuItem
                                        key={invite.orgId}
                                        onClick={(): void => {
                                            acceptInvite(invite.orgId);
                                        }}
                                    >
                                        <span className="size-4" />
                                        <span className="flex-1 truncate">{invite.orgName}</span>
                                        <span className="text-xs text-occ-foreground-tertiary">
                                            invited
                                        </span>
                                    </DropdownMenuItem>
                                ))}
                            </div>
                            <DropdownMenuItem onSelect={(event): void => {
                                event.preventDefault();
                                setCreateOrgOpen(true);
                            }}
                            >
                                <Plus className="size-4" />
                                <span>Create new organization</span>
                            </DropdownMenuItem>
                        </>
                    )}
                    {shouldShowTheme && (
                        <>
                            {(shouldShowIdentity || shouldShowOrgs) && <DropdownMenuSeparator />}
                            <DropdownMenuSub>
                                <DropdownMenuSubTrigger className="gap-2">
                                    <SunMoon className="size-4 text-occ-foreground-secondary" />
                                    <span>Theme</span>
                                </DropdownMenuSubTrigger>
                                <DropdownMenuSubContent>
                                    <ThemeRadioGroup />
                                </DropdownMenuSubContent>
                            </DropdownMenuSub>
                        </>
                    )}
                    {shouldShowAdmin && (
                        <>
                            {(shouldShowIdentity || shouldShowOrgs || shouldShowTheme) &&
                                <DropdownMenuSeparator />}
                            <DropdownMenuItem onClick={(): void => {
                                navigate('/admin/builds');
                            }}
                            >
                                <Package className="size-4" />
                                <span>Build registry</span>
                            </DropdownMenuItem>
                        </>
                    )}
                    {(shouldShowIdentity || shouldShowOrgs || shouldShowTheme ||
                        shouldShowAdmin) &&
                        <DropdownMenuSeparator />}
                    <DropdownMenuItem onClick={(): void => {
                        signOutGoogle(user?.email);
                        authSession.logout();
                        navigate('/login', { replace: true });
                    }}
                    >
                        <LogOut className="size-4" />
                        <span>Sign out</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={(): void => {
                        signOutEverywhere();
                    }}
                    >
                        <MonitorOff className="size-4" />
                        <span>Sign out on all devices</span>
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>
            <CreateOrgDialog
                open={isCreateOrgOpen}
                onOpenChange={setCreateOrgOpen}
            />
        </>
    );
};
